import React from "react";
import { ScrollText } from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  AgentCard, FeatureCallout, ChecklistCard, TimelineList,
  SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogSarahDenialAutoResolution = () => (
  <RichBlogLayout
    category="Revenue Cycle Management"
    eyebrow="Denial Management"
    title="How Sarah Resolves a Complex Ortho Denial Before Your Team Logs In"
    subtitle="Step-by-step problem solving — not template responses — turns denials from a multi-hour investigation into a draft appeal already on the desk."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        It's 6:14 AM. The biller hasn't sat down yet. The carrier portal pushes
        an ERA into the system: a partial denial on a complex ortho installment
        for a 14-year-old patient halfway through a 24-month plan. The denial
        reason is a paragraph long. The math doesn't reconcile. The notes are
        spread across three prior claims and a pre-authorization from January.
      </p>
      <p>
        On a normal morning, that one denial is an hour of work. Open the EOB.
        Read the carrier language. Pull the patient ledger. Cross-check the
        installment schedule against the original treatment plan. Find the
        original pre-auth. Decide whether it's a coding issue, a frequency
        issue, or a payment posting mismatch. Then write the appeal.
      </p>
      <p>
        On this morning, the biller walks in to find a draft appeal letter
        already attached to the patient's chart, with a one-paragraph
        explanation of what went wrong and a recommended next step. That's
        Sarah.
      </p>
    </Prose>

    <AgentCard
      name="Sarah"
      role="AI Denial Resolution · ERA/EOB → Appeal"
      initial="S"
      description="Sarah catches every incoming ERA and EOB the moment it arrives, works the denial step-by-step like a senior biller would, and lands a drafted appeal on the right person's desk before they log in. She handles the routine resolutions on her own and escalates only the exceptions — with full context."
      channels={["ERA detection", "Patient history", "Appeal drafting", "Auto-routes exceptions"]}
    />

    <Prose>
      <h2>How denials shrink your cash, quietly</h2>
      <p>
        A typical multi-provider practice sees a 5–10% denial rate on its
        claims. On 800 claims a month, that's 40–80 denials sitting in a queue,
        each one taking 30–60 minutes of biller time to investigate, document,
        and appeal. The compound cost is bigger than the labor: every day a
        denial sits unworked is a day the cash isn't in the bank, and past a
        certain age a meaningful share of those denials get written off
        outright.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 60, suffix: " min", label: "Typical staff time per complex denial" },
        { value: 0, label: "Manual entry required" },
        { value: 24, suffix: "/7", label: "Working denials around the clock" },
        { value: 1, label: "Place where the appeal lands" },
      ]}
    />

    <Prose>
      <p>
        Sarah doesn't just speed up that work. She inverts the model. Instead of
        the team handling every denial and escalating the easy ones, the team
        only sees the ones Sarah can't confidently resolve on her own.
      </p>

      <h2>Why most "denial automation" doesn't actually resolve denials</h2>
      <p>
        Plenty of tools claim to handle denials. Most of them are template
        engines: pick a denial reason code, drop in the matching letter
        template, and mail it. That works on the trivial denials and falls
        apart on the complex ones — which is where the real money sits.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Template-based denial tools"
      afterTitle="Sarah · step-by-step problem solving"
      beforeItems={[
        "Maps a denial code to a canned response and prints it",
        "Doesn't read the patient history or prior claims",
        "Stops at identification — leaves the appeal to staff",
        "Treats every denial the same; complex cases get punted",
      ]}
      afterItems={[
        "Reads the denial in context — patient history, pre-auths, ledger",
        "Iteratively investigates: evaluates, takes an action, reassesses",
        "Drafts a complete, ready-to-submit appeal letter",
        "Resolves what it can confidently; flags the rest with full context",
      ]}
    />

    <Prose>
      <h2>Six things Sarah does between the ERA arriving and an appeal landing on your desk</h2>
      <p>
        This isn't a single-pass classifier. Sarah works a denial the way a
        senior biller would: investigate, hypothesize, verify, refine. She
        keeps going until she understands the actual root cause — not just the
        category code on the EOB.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Detects the ERA or EOB the moment it lands",
          body:
            "No one on your team uploads, sorts, or routes the document. Sarah picks it up the second it arrives and starts work.",
        },
        {
          title: "Reviews the denial in full context",
          body:
            "She reads the carrier's reason, then pulls the patient's history, the original pre-authorization, the installment schedule, and prior claims for the same procedure.",
        },
        {
          title: "Verifies the ledger and posting",
          body:
            "She reconciles what was billed, what was posted, and what's still outstanding — catching the cases where the denial is actually a posting or coding error on our side.",
        },
        {
          title: "Iterates until she finds the root cause",
          body:
            "Sarah evaluates what she's found, takes an action, reassesses, and continues. She doesn't stop at the first plausible explanation. She keeps going until the cause is actually identified.",
        },
        {
          title: "Drafts a ready-to-submit appeal",
          body:
            "Once the root cause is clear, she prepares a resolution: an appeal letter with the right narrative, the right attachments referenced, and the right tone — staged for one-click submission.",
        },
        {
          title: "Routes by confidence",
          body:
            "If she's certain, she submits or queues it autonomously. If anything is uncertain, she flags the case to the right teammate with the full investigation already attached.",
        },
      ]}
    />

    <PullQuote>
      Your team is no longer stuck handling every single denial. They're only
      focusing on the exceptions.
    </PullQuote>

    <Prose>
      <h2>Walking through one complex ortho denial</h2>
      <p>
        Take a real example. Patient: a 14-year-old in month 11 of 24 of an
        ortho plan. Denial code on the EOB is benign-sounding: "missing
        documentation." A junior biller might respond with a generic appeal and
        a copy of the original treatment plan. That gets denied again two weeks
        later.
      </p>
      <p>
        Here's what Sarah actually does in seconds.
      </p>
    </Prose>

    <ChecklistCard
      title="Sarah's investigation · ortho installment denial"
      items={[
        { label: "Pull original pre-authorization (Jan 14)", status: "pass" },
        { label: "Verify installment schedule against treatment plan", status: "pass" },
        { label: "Reconcile prior 10 monthly installments posted to ledger", status: "pass" },
        { label: "Check for frequency overlap on D8080", status: "pass" },
        { label: "Identify root cause: installment claim was missing reference to pre-auth #", status: "pass" },
        { label: "Draft appeal with pre-auth attached and correct reference", status: "pass" },
      ]}
    />

    <Prose>
      <p>
        The root cause isn't "missing documentation" in the abstract. It's that
        the eleventh installment claim was submitted without referencing the
        original pre-authorization number — a tiny clerical issue buried under
        a vague carrier label. Sarah finds it because she actually reads the
        prior submissions and notices the inconsistency.
      </p>
      <p>
        Then she writes the appeal. Not a template. A specific one. With the
        pre-auth referenced, the schedule attached, and a one-paragraph
        explanation that tells the carrier exactly what they need.
      </p>

      <h2>The morning that happens because Sarah works overnight</h2>
      <p>
        Here's what the biller's chart actually looks like when they sit down
        at 7:30 AM on a Tuesday.
      </p>
    </Prose>

    <TimelineList
      title="Overnight denial activity · Sarah"
      events={[
        { time: "23:14", channel: "form", label: "ERA received from Delta Dental · 12 lines · 3 denials flagged", by: "Sarah · auto-detected" },
        { time: "23:18", channel: "form", label: "Denial 1 resolved · D2750 narrative auto-attached, claim resubmitted", by: "Sarah · confidence high" },
        { time: "23:42", channel: "form", label: "Denial 2 resolved · ortho installment appeal drafted with pre-auth", by: "Sarah · staged for biller review" },
        { time: "00:09", channel: "form", label: "Denial 3 flagged · frequency conflict, needs clinical decision", by: "Sarah → Dr. Patel · context attached" },
        { time: "06:12", channel: "form", label: "ERA received from Aetna · 8 lines · 1 denial · already drafted", by: "Sarah · ready for review" },
        { time: "07:30", channel: "form", label: "Biller logs in · 2 drafts to review, 1 clinical decision pending", by: "Asha summary" },
      ]}
    />

    <Prose>
      <h2>Management by exception — and what that does to the team</h2>
      <p>
        The shift from "handle every denial" to "handle only the exceptions" is
        the part that compounds. Your senior biller's 8 hours stop being a
        triage queue and start being a decision queue. The volume is smaller,
        the cases are harder, and the work matches the skill level.
      </p>
      <p>
        Meanwhile, the clean cases — the ones that just needed a narrative
        attached or a pre-auth referenced — are already handled. Cash hits the
        bank days earlier. Aging buckets shrink. Write-offs go down because
        denials get worked instead of getting old.
      </p>
    </Prose>

    <FeatureCallout
      stat="Hours → Seconds"
      label="From an investigation to a drafted appeal"
      description="Sarah works the denial the way a senior biller would — pulling history, verifying ledger, identifying root cause — and stages the appeal before the team arrives."
      icon={ScrollText}
    />

    <Prose>
      <h2>The loop, not the lookup</h2>
      <p>
        Basic AI tools take a denial in and spit a response out. Sarah is built
        as a problem solver. She investigates, evaluates her own findings,
        takes action, and reassesses — looping until she actually understands
        the cause. That iterative loop is what separates real denial
        resolution from a smarter form letter.
      </p>
      <p>
        And critically, she knows when to stop and ask. The cases that need a
        clinical judgment, an unusual narrative, or a conversation with the
        patient still go to a human — but with all the context already
        gathered. The team's job becomes the decision, not the discovery.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Denials don't have to be a multi-hour investigation per case. Sarah
        does the digging, finds the actual root cause, and lands the appeal on
        the right desk — usually before anyone has logged in. The team's
        morning starts with decisions, not detective work.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogSarahDenialAutoResolution;
